import React from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import ChecklistTile from '../../../../../incase-domain/components/checkbox/ChecklistTile';
import {ELEMENT, retryableQuestions} from '../../../../../incase-domain/elements/base/ElementTypes';
import {IQuestionElements} from '../../../../../incase-domain/elements/question';
import {selectedPresentationMetaSelector} from '../../../../../store/selectors/presentation.selectors';
import SectionTitle from '../../../../elements/common/SectionTitle';
import DebouncedNumberInput from '../../common/debounced/DebounceNumberInput';
import DebatableOption from './DebatableOption';
import {getAttemptsAndRetriable} from '../../../../../incase-domain/utils/retryCount';
import {DEFAULT_NUMBER_OF_RETRIES} from '../../../../../constants';

export enum QUESTION_OPTIONS {
    HAVE_TRY_BUTTON = 'haveTryButton',
    SHOW_INDEX = 'showIndex',
    SINGLE_CORRECT = 'singleCorrect',
    BLOCKING_QUESTION = 'blockingQuestion',
    SHOW_IN_REPORT = 'showInReport',
    SHOW_ALL_COMMENTS = 'showAllComments',
    SHOW_RESULTS = 'showResults',
    SHOW_CORRECT = 'showCorrect',
    SHOW_CORRECT_COUNT = 'showCorrectCount',
    SHOW_WRONG = 'showWrong',
    SHOW_WRONG_COUNT = 'showWrongCount',
    RETRY_QUESTION = 'retryQuestion',
    DEBATABLE_QUESTION = 'debatableQuestion',
    SLIDER_VIEW = 'sliderView',
    SHOW_CORRECT_ANSWERS_COUNT = 'showCorrectAnswersCount',
}

interface QuestionOptionsProps {
    highlightedElement: IQuestionElements;
    highlightedElementChangeHandler: {
        single: (field: keyof IQuestionElements | string, value: unknown) => void;
        multiple: (fields: (keyof IQuestionElements)[], values: unknown[]) => void;
    };
    questionOptions: QUESTION_OPTIONS[];
    maxNumberOfRetryAttempts?: number;
}

export default function QuestionOptions(props: QuestionOptionsProps): JSX.Element {
    const {highlightedElement, highlightedElementChangeHandler, questionOptions, maxNumberOfRetryAttempts = 10} = props;

    const selectedPresentation = useSelector(selectedPresentationMetaSelector);
    const attemptsData = getAttemptsAndRetriable(highlightedElement, selectedPresentation);

    const {t} = useTranslation();

    return (
        <div className="l-group--form">
            <SectionTitle label={t('designSidepanel.preferences')} />
            <div className="u-flex u-flex-col u-gap-2">
                {questionOptions.includes(QUESTION_OPTIONS.HAVE_TRY_BUTTON) && (
                    <ChecklistTile
                        value={highlightedElement.haveTryButton}
                        label={t('designSidepanel.tryButton')}
                        help={t('designSidepanel.tryButtonExpl')}
                        onChange={(value) => highlightedElementChangeHandler.multiple(['haveTryButton', 'retryQuestion'], [value, value])}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SHOW_CORRECT) && highlightedElement.type === ELEMENT.MULTI_SELECT && (
                    <ChecklistTile
                        value={!!highlightedElement.showCorrect}
                        label={t('designSidepanel.showCorrect')}
                        help={t('designSidepanel.showCorrectExpl')}
                        onChange={(value) => highlightedElementChangeHandler.single('showCorrect', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SHOW_CORRECT_COUNT) && highlightedElement.type === ELEMENT.MULTI_SELECT && (
                    <ChecklistTile
                        value={!!highlightedElement.showCorrectCount}
                        label={t('designSidepanel.showCorrectCount')}
                        onChange={(value) => highlightedElementChangeHandler.single('showCorrectCount', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SHOW_WRONG) && highlightedElement.type === ELEMENT.MULTI_SELECT && (
                    <ChecklistTile
                        value={!!highlightedElement.showWrong}
                        label={t('designSidepanel.showWrong')}
                        onChange={(value) => highlightedElementChangeHandler.single('showWrong', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SHOW_WRONG_COUNT) && highlightedElement.type === ELEMENT.MULTI_SELECT && (
                    <ChecklistTile
                        value={!!highlightedElement.showWrongCount}
                        label={t('designSidepanel.showWrongCount')}
                        onChange={(value) => highlightedElementChangeHandler.single('showWrongCount', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SHOW_INDEX) && (
                    <ChecklistTile
                        value={highlightedElement.writeAnswerIndices}
                        label={t('designSidepanel.showIndex')}
                        onChange={(value) => highlightedElementChangeHandler.single('writeAnswerIndices', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SINGLE_CORRECT) && (
                    <ChecklistTile
                        value={highlightedElement.singleCorrect}
                        label={t('designSidepanel.singleCorrect')}
                        help={t('designSidepanel.singleCorrectExpl')}
                        onChange={(value) => highlightedElementChangeHandler.single('singleCorrect', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.BLOCKING_QUESTION) && (
                    <ChecklistTile
                        value={!!highlightedElement.blockingQuestion}
                        label={t('designSidepanel.blockingQuestion')}
                        help={t('designSidepanel.blockingQuestionExpl')}
                        onChange={(value) => highlightedElementChangeHandler.single('blockingQuestion', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SHOW_IN_REPORT) && (
                    <ChecklistTile
                        value={!!highlightedElement.showInReport}
                        label={t('designSidepanel.showInReport')}
                        onChange={(value) => highlightedElementChangeHandler.single('showInReport', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SHOW_RESULTS) &&
                    (highlightedElement.singleCorrect || highlightedElement.haveTryButton) && (
                        <ChecklistTile
                            value={!!highlightedElement.showResults}
                            label={t('designSidepanel.showResults')}
                            help={t('designSidepanel.showResultsExpl')}
                            onChange={(value) => highlightedElementChangeHandler.single('showResults', value)}
                        />
                    )}

                {questionOptions.includes(QUESTION_OPTIONS.SHOW_ALL_COMMENTS) && (
                    <ChecklistTile
                        value={highlightedElement.showAllComments}
                        label={t('designSidepanel.showAllComments')}
                        help={t('designSidepanel.showAllCommentsExpl')}
                        onChange={(value) => highlightedElementChangeHandler.single('showAllComments', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SLIDER_VIEW) && (
                    <ChecklistTile
                        value={!!highlightedElement.sliderView}
                        label={t('designSidepanel.sliderView')}
                        help={t('designSidepanel.sliderViewExpl')}
                        onChange={(value) => highlightedElementChangeHandler.single('sliderView', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.SHOW_CORRECT_ANSWERS_COUNT) && (
                    <ChecklistTile
                        value={!!highlightedElement.showCorrectAnswersCount}
                        label={t('designSidepanel.showCorrectAnswersCount')}
                        onChange={(value) => highlightedElementChangeHandler.single('showCorrectAnswersCount', value)}
                    />
                )}

                {questionOptions.includes(QUESTION_OPTIONS.RETRY_QUESTION) && retryableQuestions.includes(highlightedElement.type) && (
                    <>
                        <div className="u-flex u-h-between u-v-center u-gap-2">
                            <ChecklistTile
                                value={attemptsData?.retriable || false}
                                label={t('designSidepanel.retryQuestion')}
                                help={t('designSidepanel.retryQuestionExpl')}
                                onChange={(value) =>
                                    highlightedElementChangeHandler.multiple(
                                        ['retryQuestion', 'noOfAttempts'],
                                        [value, value ? DEFAULT_NUMBER_OF_RETRIES : 0]
                                    )
                                }
                            />
                            {attemptsData?.retriable && (
                                <DebouncedNumberInput
                                    step={1}
                                    min={2}
                                    max={maxNumberOfRetryAttempts}
                                    onChange={(newValue) => {
                                        highlightedElementChangeHandler.multiple(['retryQuestion', 'noOfAttempts'], [true, newValue]);
                                    }}
                                    value={attemptsData.maxAttempts || DEFAULT_NUMBER_OF_RETRIES}
                                />
                            )}
                        </div>
                    </>
                )}

                {questionOptions.includes(QUESTION_OPTIONS.DEBATABLE_QUESTION) && (
                    <DebatableOption
                        highlightedElement={highlightedElement}
                        highlightedElementChangeHandler={highlightedElementChangeHandler}
                    />
                )}
            </div>
        </div>
    );
}
